import {Input, PrimaryButton} from "components/styled";
import React, {useCallback, useEffect, useState} from "react";
import styled from "styled-components";
import {useDispatch, useSelector} from "react-redux";
import {getUser, getUserRequestState} from "modules/selectors";
import {MenuItem} from "@mui/material";
import {media} from "assets/media";
import {contactUsRequest} from "modules/actions";
import {Exist} from "components/Exist";
import {useTranslation} from "react-i18next";

const Form = styled.form`
	padding-top: 26px;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	justify-content: center;
	gap: 20px;

	${Input} {
		max-width: 300px;
		width: 100%;
	}

	@media screen and ${media.lessThanSM} {
		padding: 20px 0 0 0;
		box-sizing: border-box;
		gap: 15px;

		${Input} {
			max-width: unset;
		}
	}
`;

const TextAreaWrapper = styled.div`
	width: 100%;

	${Input} {
		max-width: unset !important;
	}
`;

const SubmitWrapper = styled.div`
	width: 100%;
	display: flex;
	justify-content: flex-start;

	button {
		max-width: 280px;
		width: 100%;
		margin: 0;
		background: ${({theme}) => theme.button.primary.default};
	}

	@media screen and ${media.lessThanSM} {
		justify-content: center;

		button {
			max-width: unset;
		}
	}
`;

export const ContactUs: React.FC = () => {
	const dispatch = useDispatch();
	const {t} = useTranslation();
	const user = useSelector(getUser);
	const {isIdle, isLoading, isSuccess, isLoadingOrSuccess} =
		useSelector(getUserRequestState)("contactUs");
	const [formData, setFormData] = useState({
		name: "",
		email: "",
		type: "",
		question: "",
	});

	const onChange = useCallback(
		(e: React.ChangeEvent<HTMLFormElement>) => {
			const name = e.target.name;
			const value = String(e.target.value);

			if (!name) {
				return;
			}

			setFormData({...formData, [name]: value});
		},
		[formData]
	);

	const selectAdapter = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			onChange(e as unknown as React.ChangeEvent<HTMLFormElement>);
		},
		[onChange]
	);

	useEffect(() => {
		if (user?.id) {
			setFormData({
				...formData,
				name: `${user.firstName} ${user.lastName}`,
				email: user.email,
			});
		}
		// Disabled due need update form only if user updated, formData as dependency will affect loop
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user]);

	useEffect(() => {
		if (isSuccess) {
			setFormData({
				name: user ? `${user.firstName} ${user.lastName}` : "",
				email: user?.email || "",
				type: "",
				question: "",
			});
		}
	}, [isSuccess, user]);

	const onSubmit = useCallback(
		(e: React.SyntheticEvent) => {
			e.preventDefault();
			e.stopPropagation();
			dispatch(contactUsRequest(formData));
		},
		[dispatch, formData]
	);

	const disableSubmit = isLoadingOrSuccess || formData.question === "";

	return (
		<Form onChange={onChange} onSubmit={onSubmit}>
			<Input
				name="name"
				label={t("help.contact_us.name")}
				value={formData.name}
				disabled={Boolean(user?.firstName || user?.lastName)}
				required={true}
			/>
			<Input
				name="email"
				label={t("my_account.field.email")}
				value={formData.email}
				disabled={Boolean(user?.email)}
				required={true}
			/>
			<Input
				select
				name="type"
				label={t("help.contact_us.question_type")}
				value={formData.type}
				onChange={selectAdapter}
				required={true}>
				<MenuItem value="My Account">{t("help.contact_us.type.account")}</MenuItem>
				<MenuItem value="Logging in/Registration">{t("help.contact_us.type.login")}</MenuItem>
				<MenuItem value="My Team">{t("help.contact_us.type_my_team")}</MenuItem>
				<MenuItem value="My Leagues">{t("leagues.title.my_leagues")}</MenuItem>
				<MenuItem value="Leaderboards">{t("help.contact_us.type.leaderboards")}</MenuItem>
				<MenuItem value="Other">{t("help.contact_us.type.other")}</MenuItem>
			</Input>
			<TextAreaWrapper>
				<Input
					name="question"
					label={t("help.contact_us.question")}
					value={formData.question}
					multiline={true}
					rows={7}
					placeholder={t("help.contact_us.placeholder_text")}
					inputProps={{maxLength: 300}}
					required={true}
				/>
			</TextAreaWrapper>

			<SubmitWrapper>
				<PrimaryButton disabled={disableSubmit}>
					<Exist when={isIdle}>{t("forgot_password.modal.submit")}</Exist>
					<Exist when={isLoading}>Sending....</Exist>
					<Exist when={isSuccess}>Sent</Exist>
				</PrimaryButton>
			</SubmitWrapper>
		</Form>
	);
};
